import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Segmented, Select, Empty, Skeleton, Tag, Tooltip, Input, Button } from "antd";
import TimepointModelPane from "components/Coordination/TimepointModelPane";
import { DualLaneTimeline } from "pages/Timeline/TimelinePage";
import moment from "moment";
import { setSidebarStatus } from "actions/user";
import Arrow from "assets/images/leftArr.png";
import { getCompanyDiffs } from "api/diff/diffApi";
import { rememberProject, getRememberedProject } from "helpers/projectSelection";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import ModelEditsDrawer from "components/Coordination/ModelEditsDrawer";
import { matchesReviewFilter, todayIso } from "helpers/reviewFilter";

const T = {
  ink: "#23343E", text: "#3A4952", muted: "#8D8D8D", sub: "#AEAEAE",
  line: "#EFEFEF", line2: "#E2E2E2", bg: "#FAFAFA", wash: "#E9F1F5",
  wash2: "#F3F7F9", navy: "#455D6B", navyD: "#3A5765",
  added: "#4FC92A", added2: "#317A1B", addedBg: "#EEFBE9",
  removed: "#FB8080", removed2: "#C53030", removedBg: "#FFF0F0",
  mod: "#71A0B8", mod2: "#3A5765", modBg: "#E9F1F5",
  orange: "#FE6919",
};

function DiffBars({ added = 0, removed = 0, modified = 0, w = 200 }) {
  const total = Math.max(1, added + removed + modified);
  const pa = (added / total) * w;
  const pr = (removed / total) * w;
  const pm = (modified / total) * w;
  return (
    <div style={{ display: "flex", width: w, height: 8, borderRadius: 4, overflow: "hidden", background: "#E5E7EB" }}>
      <div style={{ width: pa, background: T.added }} />
      <div style={{ width: pm, background: T.mod }} />
      <div style={{ width: pr, background: T.removed }} />
    </div>
  );
}

function TypeTag({ type }) {
  if (type === "model") {
    return <span style={{ background: T.wash, color: T.navyD, padding: "2px 7px", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: ".04em" }}>MODEL</span>;
  }
  if (type === "est" || type === "estimate") {
    return <span style={{ background: "#FFF7E6", color: "#B45309", padding: "2px 7px", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: ".04em" }}>ESTIMATE</span>;
  }
  return <span style={{ background: T.addedBg, color: T.added2, padding: "2px 7px", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: ".04em" }}>MODEL + EST</span>;
}

/**
 * Generic diff analytics page — the three sidebar sub-routes reuse this:
 *  - /analytics/models      → kind="model"
 *  - /analytics/estimates   → kind="est"
 *  - /analytics/revisions   → kind="all" (both types, chronological)
 *
 * Data: aggregates /diff/project/:projectId across every project the user
 * can access. No new backend endpoint needed.
 */
export default function DiffAnalyticsPage({ kind = "all", pageTitle }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const currentUserId = userInfoRedux?._id || userInfoRedux?.id;

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [projects, setProjects] = useState([]);
  const [diffs, setDiffs] = useState([]);

  // Layout/filter state
  const [layout, setLayout] = useState("timeline"); // timeline | cards | table | dual
  // The same filter vocabulary the issues and checklist pages use: free
  // text, a day, "Today", and - for model pushes - whether the revision
  // has been through the Review Center yet.
  const [q, setQ] = useState("");
  const [day, setDay] = useState("");
  const [approval, setApproval] = useState("all"); // all | pending | approved
  // Start on the project the rest of the coordination suite is on. This
  // page remembered a pick but never restored one, so every visit opened
  // on "All projects" and the pick had to be made again.
  const [projectFilter, setProjectFilter] = useState([]);
  // Which push the "Show model edits" drawer is open on. One at a time:
  // each one loads real geometry for both generations.
  const [editsFor, setEditsFor] = useState(null); // { commitId, projectTitle }
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !currentUserId) return;
    seededRef.current = true;
    const remembered = getRememberedProject(currentUserId);
    if (remembered) setProjectFilter([String(remembered)]);
  }, [currentUserId]);

  // Stable key for the projectFilter array so the effect only re-runs on real changes.
  const projectFilterKey = projectFilter.join(",");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    getCompanyDiffs({
      kind,
      projectIds: projectFilter.length ? projectFilter : undefined,
    })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data || {};
        setDiffs(Array.isArray(payload.diffs) ? payload.diffs : []);
        setProjects(Array.isArray(payload.projects) ? payload.projects : []);
      })
      .catch((e) => !cancelled && setErr(e.message || "Failed to load diffs"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, projectFilterKey]);

  const allDiffs = useMemo(() => {
    const filter = { q, date: day };
    return diffs.filter((r) => {
      if (approval !== "all") {
        // Only a model push has an approval state; an estimate revision
        // is not something the Review Center gates, so it is out of
        // scope for both of those buttons rather than silently "approved".
        const status = r.commit ? (r.commit.reviewStatus || "approved") : null;
        if (status !== approval) return false;
      }
      if (!q && !day) return true;
      return matchesReviewFilter(
        {
          projectTitle: r.projectTitle,
          label: r.commit?.label || "",
          author: r.commit?.author || "",
          doc: r.commit?.revitDocTitle || "",
          status: r.status,
          calculatedAt: r.calculatedAt,
          createdAt: r.createdAt,
        },
        filter
      );
    });
  }, [diffs, q, day, approval]);
  const isToday = day === todayIso();

  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: String(p._id), label: p.title || "Untitled" })),
    [projects]
  );

  // A remembered project the user can no longer see would filter the page
  // down to nothing with no way to tell why: drop it once the list is in.
  useEffect(() => {
    if (!projects.length || !projectFilter.length) return;
    const known = new Set(projects.map((p) => String(p._id)));
    const kept = projectFilter.filter((id) => known.has(String(id)));
    if (kept.length !== projectFilter.length) setProjectFilter(kept);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  return (
    <div style={{ background: T.bg, minHeight: "calc(100 * var(--vh))" }}>
      <div className="inner-header-position">
        <div className="custom-container d-flex align-items-center justify-content-between flex-wrap">
          <div className="d-flex align-items-center">
            <div
              onClick={() => dispatch(setSidebarStatus(true))}
              className="cursor-pointer"
            >
              <img src={Arrow} className="left-arrow-img" alt="Back" />
            </div>
            <div className="d-flex align-items-center ml-3">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.navyD} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="6" cy="3" r="3"/><circle cx="6" cy="21" r="3"/><circle cx="18" cy="12" r="3"/>
                <path d="M6 6v12"/><path d="M18 9a9 9 0 0 1-9 9"/>
              </svg>
              <p className="my-heading f-14 ml-2">{pageTitle || "Diff analytics"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* The trail this page sits on. It had none - which meant the
          only way back to the project it was filtered to was the browser
          button, and the page could not be linked into from Export
          history without the reader losing their place. */}
      <div style={{ background: "#fff", padding: "10px 32px 0" }}>
        <CoordinationBreadcrumbs
          items={[
            ...(projectFilter.length === 1
              ? [{
                  label: projectOptions.find((o) => String(o.value) === String(projectFilter[0]))?.label || "Project",
                  to: `/coordination/${projectFilter[0]}`,
                }]
              : []),
          ]}
          current={pageTitle || "Diff analytics"}
        />
      </div>

      {/* Filter strip */}
      <div style={{
        background: "#fff", borderBottom: `1px solid ${T.line}`,
        padding: "10px 32px",
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      }}>
        <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em" }}>
          LAYOUT
        </div>
        <Segmented
          value={layout}
          onChange={setLayout}
          size="small"
          options={[
            // Same order as Export history, and for the same reason: an
            // investigation starts with when, not with a card grid.
            { value: "timeline", label: "Timeline" },
            { value: "cards", label: "Cards" },
            { value: "table", label: "Table" },
            { value: "dual", label: "Dual-lane" },
          ]}
        />
        <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginLeft: 8 }}>
          VIEW
        </div>
        <Segmented
          value={kind}
          size="small"
          onChange={(k) =>
            history.push(
              k === "model"
                ? "/analytics/models"
                : k === "est"
                ? "/analytics/estimates"
                : "/analytics/revisions"
            )
          }
          options={[
            { value: "all", label: "All" },
            { value: "model", label: "Models" },
            { value: "est", label: "Estimates" },
          ]}
        />
        <div style={{ fontSize: 10.5, fontWeight: 600, color: T.muted, letterSpacing: ".06em", marginLeft: 8 }}>
          PROJECTS
        </div>
        <Select
          mode="multiple"
          allowClear
          placeholder="All projects (type to search)"
          size="small"
          // antd filters a multiple Select on the option's VALUE unless
          // told otherwise, and the value here is an ObjectId - so typing
          // a project's name matched nothing at all and the picker looked
          // empty for a project that was right there in the list.
          optionFilterProp="label"
          filterOption={(input, option) =>
            String(option?.label || "").toLowerCase().includes(String(input || "").toLowerCase())
          }
          style={{ minWidth: 220, maxWidth: 360 }}
          value={projectFilter}
          onChange={(v) => {
            setProjectFilter(v);
            // Share the pick with the rest of the coordination suite.
            if (v.length) rememberProject(currentUserId, v[v.length - 1]);
          }}
          options={projectOptions}
          maxTagCount="responsive"
        />
        <span style={{ flex: 1 }} />
        <Tag color="default" style={{ margin: 0, fontSize: 11 }}>
          {kind === "model" ? "Model exports" : kind === "est" ? "Estimate revisions" : "All revisions"} · {allDiffs.length} entries
        </Tag>
      </div>

      {/* Second filter row - the same vocabulary as the issues and
          checklist pages: search, a day, Today, and the approval state of
          a model push. */}
      <div style={{
        background: "#fff", borderBottom: `1px solid ${T.line}`,
        padding: "8px 32px",
        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      }}>
        <Input.Search
          allowClear
          size="small"
          placeholder="Search by project, push label, author…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ width: 320 }}
        />
        <input
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value)}
          title="Only revisions from this day"
          style={{ border: `1px solid ${T.line2}`, borderRadius: 6, padding: "3px 8px", fontSize: 13 }}
        />
        <Button size="small" type={isToday ? "primary" : "default"}
                onClick={() => setDay(isToday ? "" : todayIso())}>
          Today
        </Button>
        <Segmented
          size="small"
          value={approval}
          onChange={setApproval}
          options={[
            { value: "all", label: "All" },
            { value: "pending", label: "Not yet approved" },
            { value: "approved", label: "Approved" },
          ]}
        />
        {(q || day || approval !== "all") && (
          <Button size="small" type="text" onClick={() => { setQ(""); setDay(""); setApproval("all"); }}>
            Clear
          </Button>
        )}
      </div>

      <div style={{ padding: "24px 32px" }}>
        {/* Dual-lane (Merge B): the /timeline page embedded as a layout.
            It fetches its own per-project commit/estimate events and
            carries its own GlobalFilterBar, so the diff rows and the
            PROJECTS multi-select above don't apply to it. */}
        {layout === "dual" ? (
          <DualLaneTimeline embedded />
        ) : loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : err ? (
          <div style={{ padding: 16, background: T.removedBg, border: "1px solid #FAD6D6", borderRadius: 6, color: T.removed2 }}>
            {err}
          </div>
        ) : allDiffs.length === 0 ? (
          <Empty description={`No ${kind === "model" ? "model" : kind === "est" ? "estimate" : ""} diffs calculated yet. Diffs generate automatically on the next export.`} />
        ) : layout === "cards" ? (
          <CardsLayout rows={allDiffs} history={history} onShowEdits={setEditsFor} />
        ) : layout === "table" ? (
          <TableLayout rows={allDiffs} history={history} onShowEdits={setEditsFor} />
        ) : (
          <TimelineLayout rows={allDiffs} history={history} onShowEdits={setEditsFor} />
        )}
      </div>

      <ModelEditsDrawer
        open={!!editsFor}
        commitId={editsFor?.commitId}
        projectTitle={editsFor?.projectTitle}
        fullscreen={!!editsFor?.fullscreen}
        initialAction={editsFor?.action || ""}
        onClose={() => setEditsFor(null)}
      />
    </div>
  );
}

// The commit a diff row was produced by - the model lane carries it two
// ways depending on which endpoint filled the row in.
export function commitIdOf(r) {
  return r?.commitId || r?.commit?._id || null;
}

// Who did it. Every lane carries `author` now (the estimation lane
// stored createdBy for years and never surfaced it), so a revision is
// never anonymous in any layout - "diff/commit/export/resolve always use
// the name of the person who did the action, so it's kept on record".
export function authorNameOf(r) {
  if (r?.author?.name) return r.author.name;
  if (r?.author?.email) return r.author.email;
  if (typeof r?.commit?.author === "string" && r.commit.author) return r.commit.author;
  return "";
}

function AuthorTag({ row, size = 11.5 }) {
  const name = authorNameOf(row);
  if (!name) return <span style={{ fontSize: size, color: T.sub }}>author not recorded</span>;
  return (
    <span style={{ fontSize: size, color: T.muted }} title={row?.author?.email || name}>
      by <span style={{ color: T.text, fontWeight: 600 }}>{name}</span>
    </span>
  );
}

// The push in words. Built once when the diff is computed
// (back/utils/editNarrative.js) so every layout says the same thing
// without re-reading a single element.
function NarrativeLine({ row, style }) {
  const n = row?.commit?.narrative;
  if (!n || !n.text) return null;
  return (
    <div style={{ fontSize: 12, color: T.text, lineHeight: 1.45, ...style }}>
      <span style={{ color: T.navyD, fontWeight: 600 }}>{n.text}</span>
      {n.approx && (
        <Tooltip title="Derived from the stored diff flags: a type swap cannot be told from a reshape until this revision is re-exported.">
          <Tag color="default" style={{ margin: "0 0 0 6px", fontSize: 10 }}>approximate</Tag>
        </Tooltip>
      )}
    </div>
  );
}

// Shared by all three layouts so the buttons read and behave the same
// wherever a revision is listed.
//
// Three doors into the same drawer:
//   Show model edits - the drawer as it always was.
//   Create issue     - the fullscreen review with the new-issue form
//                      already open, so the screenshot it files is of
//                      the revision that prompted it.
//   Full screen      - the fullscreen review, whose right rail carries
//                      both issue actions.
function RevisionButton({ label, title, onClick, subtle, primary }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={title}
      style={{
        background: subtle ? "transparent" : (primary ? T.navy : "#fff"),
        color: subtle || !primary ? T.navyD : "#fff",
        border: subtle || !primary ? `1px solid ${T.line2}` : "none",
        padding: subtle ? "4px 10px" : "6px 12px",
        borderRadius: 4, fontSize: subtle ? 11.5 : 12, fontWeight: 600,
        cursor: "pointer", whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );
}

function ShowEditsButton({ row, onShowEdits, subtle = false }) {
  const commitId = commitIdOf(row);
  if (!commitId || !onShowEdits) return null;
  const open = (extra) => onShowEdits({ commitId, projectTitle: row.projectTitle, ...extra });
  return (
    <>
      <RevisionButton
        label="Show model edits" subtle={subtle} primary
        title="Open the changed fragments in 3D, with the previous version underneath"
        onClick={() => open({})}
      />
      <RevisionButton
        label="Create issue" subtle={subtle}
        title="Open this revision full screen and raise an issue against it, with a screenshot of the view"
        onClick={() => open({ fullscreen: true, action: "create" })}
      />
      <RevisionButton
        label="Full screen" subtle={subtle}
        title="Open this revision full screen - the right rail can create or close an issue from the view"
        onClick={() => open({ fullscreen: true })}
      />
    </>
  );
}

// ── Cards layout (V1) ──────────────────────────────────────────────────────
function CardsLayout({ rows, history, onShowEdits }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {rows.map((r) => {
        const s = r.summary || {};
        const p = r.pricing && r.pricing.totalPrice;
        return (
          <div key={r._id} style={{
            background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8,
            display: "grid", gridTemplateColumns: "90px 1fr 240px 168px",
            overflow: "hidden",
          }}>
            <div style={{
              background: T.wash2, borderRight: `1px solid ${T.line}`,
              padding: "14px 12px",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: T.navyD }}>
                {moment(r.calculatedAt || r.exportDate).format("D")}
              </div>
              <div style={{ fontSize: 10.5, fontWeight: 500, color: T.navyD, textTransform: "uppercase", letterSpacing: ".06em" }}>
                {moment(r.calculatedAt || r.exportDate).format("MMM")}
              </div>
              <div style={{ fontSize: 10, color: T.muted }}>
                {moment(r.calculatedAt || r.exportDate).format("h:mm A")}
              </div>
            </div>

            <div style={{ padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                <TypeTag type={r.kind} />
                <span style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>
                  {r.projectTitle}
                </span>
                {r.commit && (
                  <span style={{ fontSize: 12, color: T.text }}>
                    {r.commit.label || r.commit.revitDocTitle || "Model push"}
                    {r.commit.changedTiles ? <span style={{ color: T.muted }}> · {r.commit.changedTiles} tile{r.commit.changedTiles === 1 ? "" : "s"}</span> : null}
                  </span>
                )}
                <AuthorTag row={r} size={12} />
                {r.commit && r.commit.scope === "selection" && (
                  <Tooltip title="A selection push - only the elements the author picked, so it cannot report deletions">
                    <Tag color="default" style={{ margin: 0, fontSize: 10 }}>selection</Tag>
                  </Tooltip>
                )}
                {r.commit && r.commit.reviewStatus === "pending" && (
                  <Tag color="orange" style={{ margin: 0, fontSize: 10 }}>awaiting approval</Tag>
                )}
                {r.status === "completed" && s.hasChanges === false && (
                  <Tag color="default" style={{ margin: 0, fontSize: 10 }}>Baseline / no changes</Tag>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 12, color: T.text, marginBottom: 8 }}>
                <span style={{ color: T.added2, fontWeight: 600 }}>+{s.componentsAdded || 0}</span>
                <span style={{ color: T.removed2, fontWeight: 600 }}>−{s.componentsRemoved || 0}</span>
                <span style={{ color: T.mod2, fontWeight: 600 }}>~{s.componentsModified || 0}</span>
                <DiffBars
                  added={s.componentsAdded || 0}
                  removed={s.componentsRemoved || 0}
                  modified={s.componentsModified || 0}
                />
              </div>
              <NarrativeLine row={r} style={{ marginBottom: 8 }} />
              {p && (
                <div style={{ fontSize: 11.5, color: T.muted }}>
                  Estimate{" "}
                  <strong style={{ color: T.ink }}>
                    ${Math.round((p.newValue || 0)).toLocaleString()}
                  </strong>
                  {p.direction && p.direction !== "unchanged" && (
                    <span style={{
                      color: p.direction === "increase" ? T.added2 : T.removed2,
                      marginLeft: 8,
                      fontWeight: 600,
                    }}>
                      {p.direction === "increase" ? "+" : "−"}${Math.abs(p.difference || 0).toLocaleString()}
                      {" "}({p.percentChange})
                    </span>
                  )}
                </div>
              )}
            </div>

            <div style={{
              padding: "14px 16px",
              borderLeft: `1px solid ${T.line}`,
              background: T.bg,
              display: "flex", flexDirection: "column", gap: 4, justifyContent: "center",
            }}>
              <div style={{ fontSize: 10.5, color: T.muted, fontWeight: 600, letterSpacing: ".06em" }}>
                TOP CHANGE
              </div>
              <div style={{ fontSize: 12, color: T.ink, fontWeight: 500 }}>
                {(r.topChangesPreview && r.topChangesPreview[0]?.name) || "—"}
              </div>
            </div>

            <div style={{
              padding: "14px 16px",
              borderLeft: `1px solid ${T.line}`,
              display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "center", gap: 6,
            }}>
              <ShowEditsButton row={r} onShowEdits={onShowEdits} />
              <button
                onClick={() => history.push(`/exports/history/${r.projectId}`)}
                style={{
                  background: T.navy, color: "#fff", border: "none",
                  padding: "6px 12px", borderRadius: 4, fontSize: 12,
                  fontWeight: 600, cursor: "pointer",
                }}
              >
                Open
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Table layout ──────────────────────────────────────────────────────────
function TableLayout({ rows, history, onShowEdits }) {
  return (
    <div style={{ background: "#fff", border: `1px solid ${T.line}`, borderRadius: 6, overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead style={{ background: T.bg, borderBottom: `1px solid ${T.line}` }}>
          <tr>
            <th style={thStyle}>Date</th>
            <th style={thStyle}>Project</th>
            <th style={thStyle}>Type</th>
            <th style={thStyle}>Changed by</th>
            <th style={{ ...thStyle, textAlign: "right" }}>Δ components</th>
            <th style={{ ...thStyle, textAlign: "right" }}>$ change</th>
            <th style={thStyle}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const s = r.summary || {};
            const p = r.pricing && r.pricing.totalPrice;
            return (
              <tr key={r._id} style={{ borderBottom: `1px solid ${T.line}`, background: i % 2 ? "#fff" : "#FCFCFC" }}>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 500 }}>{moment(r.calculatedAt || r.exportDate).format("MMM D, YYYY")}</div>
                  <div style={{ fontSize: 10.5, color: T.muted }}>{moment(r.calculatedAt || r.exportDate).format("h:mm A")}</div>
                </td>
                <td style={{ ...tdStyle, fontWeight: 500, color: T.ink }}>
                  {r.projectTitle}
                  <NarrativeLine row={r} style={{ fontWeight: 400, marginTop: 3, maxWidth: 420 }} />
                </td>
                <td style={tdStyle}><TypeTag type={r.kind} /></td>
                <td style={{ ...tdStyle, whiteSpace: "nowrap" }}><AuthorTag row={r} /></td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  <span style={{ color: T.added2, fontWeight: 600 }}>+{s.componentsAdded || 0}</span>{" "}
                  <span style={{ color: T.removed2, fontWeight: 600 }}>−{s.componentsRemoved || 0}</span>{" "}
                  <span style={{ color: T.mod2, fontWeight: 600 }}>~{s.componentsModified || 0}</span>
                </td>
                <td style={{ ...tdStyle, textAlign: "right", color: p ? (p.direction === "increase" ? T.added2 : T.removed2) : T.sub, fontWeight: 600 }}>
                  {p ? `${p.direction === "increase" ? "+" : p.direction === "decrease" ? "−" : ""}$${Math.abs(p.difference || 0).toLocaleString()}` : "—"}
                </td>
                <td style={{ ...tdStyle, textAlign: "right" }}>
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                  <ShowEditsButton row={r} onShowEdits={onShowEdits} subtle />
                  <button
                    onClick={() => history.push(`/exports/history/${r.projectId}`)}
                    style={{ background: "transparent", border: `1px solid ${T.line2}`, color: T.navyD, padding: "4px 10px", borderRadius: 4, fontSize: 11.5, cursor: "pointer" }}
                  >
                    Review →
                  </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
const thStyle = { textAlign: "left", padding: "10px 12px", fontWeight: 600, fontSize: 11.5, color: T.text };
const tdStyle = { padding: "10px 12px", verticalAlign: "middle" };

// ── Timeline layout ───────────────────────────────────────────────────────
function TimelineLayout({ rows, history, onShowEdits }) {
  // One open viewer at a time - a history is long and each model is a
  // real stream.
  const [openId, setOpenId] = useState(null);
  return (
    <div style={{ position: "relative", paddingLeft: 24 }}>
      <div style={{ position: "absolute", left: 10, top: 6, bottom: 6, width: 2, background: T.line }} />
      {rows.map((r) => {
        const s = r.summary || {};
        return (
          <div key={r._id} style={{ position: "relative", paddingBottom: 16 }}>
            <div style={{
              position: "absolute", left: -20, top: 14,
              width: 14, height: 14, borderRadius: "50%",
              background: r.kind === "model" ? T.navy : r.kind === "est" ? "#F59E0B" : T.added,
              border: "3px solid #fff", boxShadow: `0 0 0 1px ${T.line2}`,
            }} />
            <div style={{ background: "#fff", border: `1px solid ${T.line}`, borderRadius: 8, padding: "12px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                <TypeTag type={r.kind} />
                <span style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>{r.projectTitle}</span>
                <span style={{ fontSize: 11, color: T.muted }}>
                  {moment(r.calculatedAt || r.exportDate).format("MMM D, YYYY · h:mm A")}
                </span>
                <AuthorTag row={r} />
                {r.commit?.label || r.commit?.revitDocTitle ? (
                  <span style={{ fontSize: 11.5, color: T.muted }}>· {r.commit.label || r.commit.revitDocTitle}</span>
                ) : null}
              </div>
              <NarrativeLine row={r} style={{ marginBottom: 8 }} />
              <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12, flexWrap: "wrap" }}>
                <span style={{ color: T.added2, fontWeight: 600 }}>+{s.componentsAdded || 0}</span>
                <span style={{ color: T.removed2, fontWeight: 600 }}>−{s.componentsRemoved || 0}</span>
                <span style={{ color: T.mod2, fontWeight: 600 }}>~{s.componentsModified || 0}</span>
                <DiffBars added={s.componentsAdded || 0} removed={s.componentsRemoved || 0} modified={s.componentsModified || 0} />
                <span style={{ flex: 1 }} />
                <ShowEditsButton row={r} onShowEdits={onShowEdits} subtle />
                <button
                  onClick={() => setOpenId(openId === r._id ? null : r._id)}
                  style={{ background: "transparent", border: `1px solid ${T.line2}`, color: T.navyD, padding: "4px 10px", borderRadius: 4, fontSize: 11.5, cursor: "pointer" }}
                >
                  {openId === r._id ? "Hide model" : "View model at this point"}
                </button>
                <button
                  onClick={() => history.push(`/exports/history/${r.projectId}`)}
                  style={{ background: "transparent", border: `1px solid ${T.line2}`, color: T.navyD, padding: "4px 10px", borderRadius: 4, fontSize: 11.5, cursor: "pointer" }}
                >
                  Review diff →
                </button>
              </div>
              {/* Every point on the timeline can be looked at as the
                  model it produced, with its own diff painted on -
                  loaded on demand, one at a time, so scrolling a long
                  history does not start twenty viewers. */}
              {openId === r._id && (
                <div style={{ marginTop: 12 }}>
                  <TimepointModelPane
                    projectId={r.projectId}
                    commitId={r.commitId || r.commit?._id}
                    label={`this revision`}
                    height={380}
                    autoLoad
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
